import { TextField, Toggle } from "@fluentui/react";
import { t } from "i18next";

import ConditionBuilder from "./ConditionBuilder";
import DevicePicker from "./DevicePicker";
import { ReadField } from "./ReadOnly";
import { NO_FIELD_PROPS } from "../../helpers/hangingProtocol/hpFormFields";
import { GROUP_KIND } from "../../helpers/hangingProtocol/protocolEnums";
import "./editors.css";

/**
 * The group envelope: what decides whether any of its protocols are considered
 * at all.
 *
 * `hpDrag` and `hpComparison` groups carry a reduced key set — no devices, no
 * conditions, no skip flag — so those controls are not merely disabled but
 * absent. Writing them would produce a group MedDream silently ignores.
 *
 * Exported section by section as well as whole: the wizard renders both sections
 * in one step, but each keeps its own heading so the step stays readable.
 */

/**
 * Name, description, active state and — for standard groups — devices.
 *
 * `badge` and `kindControl` are the group's kind, which the wizard owns because
 * it decides the whole key set: a read-only chip beside the heading once the
 * group exists, a live dropdown while it is being created.
 *
 * `field` is the wizard's Formik bag, which reports a field's error on the field
 * itself — see createFieldProps. Optional, so the composed editor and anything
 * rendering a group outside a form stay unchanged.
 */
export function GroupDetailsSection({
  group,
  kind = GROUP_KIND.STANDARD,
  onChange,
  disabled = false,
  badge = null,
  kindControl = null,
  field = NO_FIELD_PROPS,
}) {
  if (!group) return null;

  const set = (patch) => onChange({ ...group, ...patch });

  return (
    <div className="hpv2-editor-section hpv2-editor-section-first">
      <div className="hpv2-editor-section-header">
        <h4 className="hpv2-editor-heading">{t("hanging-protocol.basics")}</h4>
        {badge}
      </div>

      {kindControl && (
        <>
          <div className="hpv2-editor-row hpv2-editor-row-narrow">{kindControl}</div>
          <p className="hpv2-editor-field-note">{t("hanging-protocol.type-note")}</p>
        </>
      )}

      {/* Read as text and written as fields — the same pair of values either way,
          in the same two columns, so the step does not move between the modes. */}
      <div className="hpv2-editor-row">
        {disabled ? (
          <>
            <ReadField label={t("hanging-protocol.name")} value={group.name} />
            <ReadField label={t("hanging-protocol.description")} value={group.description} />
          </>
        ) : (
          <>
            <TextField
              label={t("hanging-protocol.name")}
              required
              value={group.name ?? ""}
              onChange={(_e, next) => set({ name: next ?? "" })}
              {...field("name")}
            />
            <TextField
              label={t("hanging-protocol.description")}
              value={group.description ?? ""}
              onChange={(_e, next) => set({ description: next ?? "" })}
              {...field("description")}
            />
          </>
        )}
      </div>

      {/* A special group has no devices, so the row goes with them. */}
      {kind === GROUP_KIND.STANDARD && (
        <>
          <div className="hpv2-editor-row hpv2-editor-row-devices">
            {/* An empty list means every device, which is what MedDream stores
                as [] — so "none selected" is not a broken state. */}
            <DevicePicker
              value={group.devices}
              disabled={disabled}
              onChange={(devices) => set({ devices })}
            />
          </div>
          {/* The note tells you how to answer, so it goes with the question. */}
          {!disabled && (
            <p className="hpv2-editor-field-note">{t("hanging-protocol.devices-note")}</p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Whether the group is live at all.
 *
 * Last on the step and a section of its own: it switches the whole group off
 * rather than editing any field above it, and beside Devices it read as part of
 * the device choice.
 */
export function GroupActiveSection({ group, onChange, disabled = false }) {
  if (!group) return null;

  return (
    <div className="hpv2-editor-section hpv2-editor-section-state">
      {disabled ? (
        /* The words the group list uses for the same state, so a group reads the
           same on the row it came from and on the page it opens. */
        <ReadField
          label={t("hanging-protocol.active")}
          value={t(group.active !== false ? "hospital.active" : "hospital.inactive")}
        />
      ) : (
        <Toggle
          label={t("hanging-protocol.active")}
          checked={group.active !== false}
          onChange={(_e, checked) => onChange({ ...group, active: Boolean(checked) })}
        />
      )}
    </div>
  );
}

/** When the group applies at all, and whether it is only a fallback. */
export function GroupConditionsSection({ group, onChange, disabled = false }) {
  if (!group) return null;

  const set = (patch) => onChange({ ...group, ...patch });

  return (
    <div className="hpv2-editor-section">
      <div className="hpv2-editor-section-header">
        <h4 className="hpv2-editor-heading">{t("hanging-protocol.when-applies")}</h4>
      </div>
      {/* Tells you what leaving the builder empty will do, which is only a question
          while there is a builder. Reading it back, the sentence under this says
          "applies always" and has said the same thing already. */}
      {!disabled && <p className="hpv2-editor-note">{t("hanging-protocol.when-applies-note")}</p>}

      {/* The section heading names this builder, so it carries no label of its
          own — its header row is left to the text-mode switch. */}
      <ConditionBuilder
        slot="groupConditions"
        value={group.groupConditions}
        disabled={disabled}
        onChange={(next) => set({ groupConditions: next })}
      />

      {disabled ? (
        /* The switch already names both of its states, so reading it back is a
           matter of printing the one it is in. */
        <ReadField
          label={t("hanging-protocol.skip-if-other-group")}
          value={t(
            group.skipIfOtherGroupHasApplicableHp
              ? "hanging-protocol.skip-if-other-group-on"
              : "hanging-protocol.skip-if-other-group-off"
          )}
        />
      ) : (
        <>
          <Toggle
            label={t("hanging-protocol.skip-if-other-group")}
            onText={t("hanging-protocol.skip-if-other-group-on")}
            offText={t("hanging-protocol.skip-if-other-group-off")}
            checked={Boolean(group.skipIfOtherGroupHasApplicableHp)}
            onChange={(_e, checked) => set({ skipIfOtherGroupHasApplicableHp: Boolean(checked) })}
          />
          <p className="hpv2-editor-field-note">{t("hanging-protocol.skip-if-other-group-note")}</p>
        </>
      )}
    </div>
  );
}

export default function GroupEditor({ group, kind = GROUP_KIND.STANDARD, onChange, disabled = false }) {
  if (!group) return null;

  return (
    <div className="hpv2-editor">
      <GroupDetailsSection group={group} kind={kind} disabled={disabled} onChange={onChange} />
      {kind === GROUP_KIND.STANDARD && (
        <GroupConditionsSection group={group} disabled={disabled} onChange={onChange} />
      )}
      <GroupActiveSection group={group} disabled={disabled} onChange={onChange} />
    </div>
  );
}
